import React from 'react';
import {
  Box,
  LinearProgress,
  Chip,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  stepConnectorClasses,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';

interface ProgressStep {
  id: string;
  label: string;
  icon: React.ReactNode;
  completed: boolean;
  current: boolean;
}

interface ProgressBarProps {
  steps: ProgressStep[];
  currentStep: number;
  totalSteps: number;
  completedSteps: number;
}

const QontoConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 10,
    left: 'calc(-50% + 16px)',
    right: 'calc(50% + 16px)',
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .line`]: {
      borderColor: theme.palette.primary.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .line`]: {
      borderColor: theme.palette.success.main,
    },
  },
  [`& .line`]: {
    borderColor: theme.palette.grey[300],
    borderTopWidth: 2,
    borderRadius: 1,
  },
}));

const QontoStepIconRoot = styled('div')<{ ownerState: { active?: boolean; completed?: boolean } }>(
  ({ theme, ownerState }) => ({
    color: theme.palette.grey[400],
    display: 'flex',
    height: 22,
    alignItems: 'center',
    ...(ownerState.active && {
      color: theme.palette.primary.main,
    }),
    ...(ownerState.completed && {
      color: theme.palette.success.main,
    }),
  }),
);

function QontoStepIcon(props: { icon: React.ReactNode; active?: boolean; completed?: boolean }) {
  const { icon, active, completed } = props;

  return (
    <QontoStepIconRoot ownerState={{ active, completed }}>
      {icon}
    </QontoStepIconRoot>
  );
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  steps,
  currentStep,
  totalSteps,
  completedSteps,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const progressPercentage = (completedSteps / totalSteps) * 100;

  return (
    <Box sx={{ width: '100%', mb: isMobile ? 1.5 : 2, overflow: 'hidden' }}>
      {/* Barra di progresso lineare */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: isMobile ? 0.5 : 1, gap: 1 }}>
        <Box sx={{ width: '100%', minWidth: 0 }}>
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            sx={{
              height: isMobile ? 6 : 8,
              borderRadius: 4,
              backgroundColor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                background: 'linear-gradient(90deg, #4caf50 0%, #2196f3 100%)',
              },
            }}
          />
        </Box>
        <Chip
          label={`${completedSteps}/${totalSteps}`}
          color="primary"
          size={isMobile ? 'small' : 'medium'}
          sx={{ 
            minWidth: isMobile ? 50 : 60, 
            fontWeight: 'bold',
            fontSize: isMobile ? '0.75rem' : '0.875rem',
          }}
        />
      </Box>

      {/* Stepper orizzontale - Solo icone */}
      <Stepper
        activeStep={currentStep}
        connector={<QontoConnector />}
        sx={{
          '& .MuiStepLabel-root': {
            padding: 0,
            overflow: 'visible',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          },
          '& .MuiStepLabel-labelContainer': {
            display: 'none', // Nasconde completamente le etichette
          },
          '& .MuiStepLabel-label': {
            display: 'none', // Nasconde completamente il testo
          },
          '& .MuiStep-root': {
            overflow: 'visible',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          },
          overflow: 'visible',
        }}
      >
        {steps.map((step) => (
          <Step key={step.id}>
            <StepLabel
              StepIconComponent={(props) => (
                <QontoStepIcon
                  {...props}
                  icon={step.icon}
                  active={step.current}
                  completed={step.completed}
                />
              )}
            >
              {/* Testo rimosso - solo icone */}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};

export default ProgressBar;
